import { Shield } from 'lucide-react';

export default function PrivacyContent() {
  return (
    <div className="prose prose-sm prose-invert max-w-none">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-800">
        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Shield className="text-white" size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white m-0">Privacy Policy</h1>
          <p className="text-sm text-gray-400 mt-0.5 m-0">นโยบายความเป็นส่วนตัว</p>
        </div>
      </div>

      {/* Intro */}
      <div className="bg-green-900/20 border border-green-700/50 rounded p-4 mb-6">
        <p className="text-green-300 text-sm leading-relaxed m-0">
          <strong>HitzLink</strong> ให้ความสำคัญกับความเป็นส่วนตัวของท่าน 
          เราจัดทำนโยบายนี้เพื่ออธิบายวิธีการที่เราเก็บรวบรวม ใช้ และปกป้องข้อมูลส่วนบุคคลของท่าน
        </p>
      </div>

      {/* Content */}
      <div className="space-y-6 text-sm">
        {/* Section 1 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. ข้อมูลที่เราเก็บรวบรวม</h2>
          <div className="space-y-3">
            <div>
              <p className="text-white font-medium mb-2">1.1 ข้อมูลที่ท่านให้โดยตรง</p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>ข้อมูลการสมัครสมาชิก: ชื่อ, อีเมล, รหัสผ่าน (เข้ารหัส)</li>
                <li>ข้อมูลการชำระเงิน: ชื่อบัญชี, เลขที่บัญชี</li>
                <li>ข้อมูลโปรไฟล์: รูปโปรไฟล์, เบอร์โทรศัพท์ (ถ้ามี)</li>
                <li>เนื้อหาที่สร้าง: Landing Pages, Components, Settings</li>
              </ul>
            </div>

            <div>
              <p className="text-white font-medium mb-2">1.2 ข้อมูลที่เก็บอัตโนมัติ</p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>Log Data: IP Address, Browser, Device, OS</li>
                <li>Cookies: Session, Auth Token, Preferences</li>
                <li>Analytics: Page Views, Clicks, Conversions</li>
              </ul>
            </div>

            <div>
              <p className="text-white font-medium mb-2">1.3 ข้อมูลจาก Third-Party Integrations</p>
              <p className="text-gray-400 text-xs mb-2">
                เมื่อท่านเชื่อมต่อบัญชีบุคคลที่สาม (Google Tag Manager, Meta CAPI) เราจะเก็บ:
              </p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>
                  <strong>Google account:</strong> อีเมล, OAuth refresh token, รายชื่อ GTM
                  accounts/containers/workspaces ที่ท่านมีสิทธิ์แก้ไข
                </li>
                <li>
                  <strong>Meta (Facebook):</strong> Pixel IDs, CAPI access tokens, Conversion API dataset IDs
                </li>
                <li>
                  รายละเอียดเชิงลึกของขอบเขตและการใช้งาน Google API ดูได้ใน{' '}
                  <span className="text-blue-300">หัวข้อ 8 — Google API Services</span> ด้านล่าง
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. วัตถุประสงค์การใช้ข้อมูล</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
              <p className="text-blue-400 font-medium mb-2">🔧 การให้บริการ</p>
              <ul className="text-gray-300 space-y-1">
                <li>• สร้างและจัดการบัญชี</li>
                <li>• ให้บริการ Landing Page Builder</li>
                <li>• ประมวลผลการชำระเงิน</li>
              </ul>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
              <p className="text-purple-400 font-medium mb-2">📊 การปรับปรุงบริการ</p>
              <ul className="text-gray-300 space-y-1">
                <li>• วิเคราะห์พฤติกรรมการใช้งาน</li>
                <li>• แก้ไขบั๊กและปัญหา</li>
                <li>• พัฒนาฟีเจอร์ใหม่</li>
              </ul>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
              <p className="text-green-400 font-medium mb-2">📧 การสื่อสาร</p>
              <ul className="text-gray-300 space-y-1">
                <li>• ส่งอีเมลยืนยัน</li>
                <li>• แจ้งเตือนหมดอายุ</li>
                <li>• ตอบคำถาม Support</li>
              </ul>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
              <p className="text-red-400 font-medium mb-2">🔒 ความปลอดภัย</p>
              <ul className="text-gray-300 space-y-1">
                <li>• ป้องกันการฉ้อโกง</li>
                <li>• ตรวจจับการโจมตี</li>
                <li>• ปฏิบัติตามกฎหมาย</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. การแบ่งปันข้อมูล</h2>
          <div className="bg-blue-900/20 border border-blue-700/50 rounded p-3 mb-3">
            <p className="text-blue-300 text-xs font-medium">
              🔒 เราไม่ขายข้อมูลส่วนบุคคลของท่านให้กับบุคคลที่สาม
            </p>
          </div>
          <p className="text-gray-300 text-xs mb-2">กรณีที่เราอาจแบ่งปันข้อมูล:</p>
          <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
            <li>Service Providers (sub-processors): Supabase (database hosting), Cloudflare (CDN/WAF), Railway (compute), Sentry (error monitoring)</li>
            <li>Payment Processors: Omise / ธนาคาร / Payment Gateway</li>
            <li>
              Third-party APIs ที่ท่านเป็นคน initiate การเชื่อมต่อด้วยตนเอง:
              Google Tag Manager API, Meta Conversions API
            </li>
            <li>Legal Requirements: หน่วยงานกฎหมาย (เมื่อมีคำสั่งศาล)</li>
          </ul>
          <p className="text-gray-400 text-xs mt-2">
            เราไม่ส่งข้อมูลที่ได้รับจาก Google APIs ไปยังบุคคลที่สามเพื่อโฆษณา การวิเคราะห์ทางการตลาด
            หรือเพื่อใช้งานนอกเหนือจากการให้บริการหลักของ HitzLink — ดูรายละเอียดใน{' '}
            <span className="text-blue-300">หัวข้อ 8 — Google API Services</span>
          </p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Cookies และเทคโนโลยีติดตาม</h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-800/30 rounded p-2">
              <p className="text-white font-medium">🍪 Essential</p>
              <p className="text-gray-400 text-xs">Authentication</p>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <p className="text-white font-medium">⚙️ Functional</p>
              <p className="text-gray-400 text-xs">Settings, Theme</p>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <p className="text-white font-medium">📊 Analytics</p>
              <p className="text-gray-400 text-xs">Google Analytics</p>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <p className="text-white font-medium">📢 Advertising</p>
              <p className="text-gray-400 text-xs">FB, TikTok Pixels</p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. ความปลอดภัยของข้อมูล</h2>
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">🔐 HTTPS/TLS</p>
              <p className="text-gray-400">Encryption</p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">🔒 bcrypt</p>
              <p className="text-gray-400">Password Hash</p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">🛡️ RLS</p>
              <p className="text-gray-400">Database Security</p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">🔑 JWT</p>
              <p className="text-gray-400">Token Auth</p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">☁️ Backup</p>
              <p className="text-gray-400">Daily</p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded p-2 text-center">
              <p className="text-white font-medium">🚨 Monitoring</p>
              <p className="text-gray-400">24/7</p>
            </div>
          </div>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. สิทธิของท่าน (PDPA)</h2>
          <div className="space-y-2 text-xs">
            <div className="bg-gray-800/30 rounded p-2">
              <span className="text-blue-400 font-medium">📖 เข้าถึงข้อมูล:</span>
              <span className="text-gray-300 ml-2">ขอเข้าถึงและขอสำเนาข้อมูลของท่าน</span>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <span className="text-green-400 font-medium">✏️ แก้ไขข้อมูล:</span>
              <span className="text-gray-300 ml-2">แก้ไขข้อมูลที่ไม่ถูกต้อง</span>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <span className="text-red-400 font-medium">🗑️ ลบข้อมูล:</span>
              <span className="text-gray-300 ml-2">ขอลบข้อมูลและบัญชี</span>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <span className="text-orange-400 font-medium">🚫 คัดค้าน:</span>
              <span className="text-gray-300 ml-2">คัดค้านการใช้ข้อมูลเพื่อการตลาด</span>
            </div>
            <div className="bg-gray-800/30 rounded p-2">
              <span className="text-purple-400 font-medium">📦 โอนย้าย:</span>
              <span className="text-gray-300 ml-2">ขอส่งออกข้อมูล (JSON/CSV)</span>
            </div>
          </div>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. การเก็บรักษาข้อมูล</h2>
          <p className="text-gray-300 text-xs">
            บัญชีที่ใช้งาน: ตลอดระยะเวลาที่ใช้บริการ • 
            บัญชีไม่ใช้งาน: 1 ปี • 
            ข้อมูลการชำระเงิน: 7 ปี (ตามกฎหมาย) • 
            Log Data: 90 วัน • 
            Google OAuth refresh token: ลบทันทีเมื่อท่านกด Disconnect, หรือเมื่อปิดบัญชี HitzLink
          </p>
        </section>

        {/* Section 8 — Google API Services + Limited Use Disclosure.
            REQUIRED for Google OAuth verification when requesting any
            sensitive scope (we ask for `tagmanager.edit.containers`).
            Do not remove without coordinating with the Google OAuth
            verification submission. The English block reproduces
            Google's mandated Limited Use language verbatim — do NOT
            paraphrase. */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-3">
            8. Google API Services & Limited Use Disclosure
          </h2>

          <div className="bg-blue-900/20 border border-blue-700/50 rounded p-4 mb-4">
            <p className="text-blue-300 text-xs font-medium mb-1">
              📌 สรุปสั้นๆ
            </p>
            <p className="text-gray-300 text-xs leading-relaxed">
              เมื่อท่านกด <em>"Connect Google"</em> ในหน้า Meta CAPI / GTM ของ HitzLink
              เราจะขอสิทธิ์ <code className="px-1.5 py-0.5 rounded bg-gray-800 text-blue-200">tagmanager.edit.containers</code>{' '}
              เพื่อสร้าง / อัปเดต tags, triggers และ variables ใน Google Tag Manager
              container ที่ท่านเป็นเจ้าของเท่านั้น เราไม่อ่านข้อมูลจาก Google services อื่นๆ
              และไม่ใช้ข้อมูลจาก Google APIs ในการฝึก AI หรือการตลาด
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-white font-medium mb-2 text-sm">8.1 ข้อมูลและขอบเขต (Scope) ที่เราขอ</p>
              <ul className="list-disc list-inside ml-3 space-y-1.5 text-gray-300 text-xs">
                <li>
                  <code className="px-1.5 py-0.5 rounded bg-gray-800 text-blue-200">openid</code>{' '}
                  + <code className="px-1.5 py-0.5 rounded bg-gray-800 text-blue-200">email</code>{' '}
                  — เพื่อแสดง "Connected as user@example.com" ในแอป
                  ช่วยให้ท่านยืนยันได้ว่าเชื่อมต่อ Google account ที่ถูกต้อง
                </li>
                <li>
                  <code className="px-1.5 py-0.5 rounded bg-gray-800 text-blue-200">
                    https://www.googleapis.com/auth/tagmanager.edit.containers
                  </code>{' '}
                  — สร้าง / อ่าน / แก้ไข tags, triggers, variables, workspaces, versions
                  ใน GTM container ที่ท่านมีสิทธิ์ จะ <strong>ไม่</strong>{' '}
                  ขอสิทธิ์ publish — ทุก version ที่เราสร้างต้องให้ท่านกด Publish เองใน
                  GTM console
                </li>
              </ul>
            </div>

            <div>
              <p className="text-white font-medium mb-2 text-sm">8.2 วิธีที่เราใช้ข้อมูล</p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>สร้าง / อัปเดต Pixel tags, conversion triggers, dataLayer variables ใน workspace ของท่าน</li>
                <li>สร้าง workspace ชื่อ "HitzLink Managed" ครั้งแรกที่ท่าน sync เพื่อแยกการเปลี่ยนแปลงของเราออกจาก work อื่นของท่าน</li>
                <li>แสดงรายชื่อ accounts / containers ของท่านในหน้า picker เพื่อให้ท่านเลือก container ที่จะ sync</li>
                <li>เก็บ refresh token เพื่อให้ระบบ sync ทำงานในเบื้องหลังได้โดยที่ท่านไม่ต้อง re-authenticate ทุกครั้ง</li>
              </ul>
            </div>

            <div>
              <p className="text-white font-medium mb-2 text-sm">8.3 วิธีที่เรา <em>ไม่</em> ใช้ข้อมูล</p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>เราไม่ขายหรือโอนข้อมูลที่ได้จาก Google APIs ให้บุคคลที่สาม</li>
                <li>เราไม่ใช้ข้อมูลจาก Google APIs ในการโฆษณา การวิเคราะห์ทางการตลาด หรือ data brokering</li>
                <li>เราไม่ใช้ข้อมูลจาก Google APIs ในการฝึก AI / Machine Learning models</li>
                <li>มนุษย์ของเราไม่อ่านข้อมูล Google APIs ของท่าน ยกเว้น (ก) เพื่อ debug ปัญหาที่ท่านรายงานเข้ามาด้วยความยินยอมของท่าน, (ข) เพื่อรักษาความปลอดภัย, หรือ (ค) เมื่อมีคำสั่งศาล</li>
              </ul>
            </div>

            <div>
              <p className="text-white font-medium mb-2 text-sm">8.4 การเก็บรักษาและการลบ</p>
              <ul className="list-disc list-inside ml-3 space-y-1 text-gray-300 text-xs">
                <li>Refresh token เก็บใน Postgres ของเรา (Supabase, region Singapore) ภายใต้ Row-Level Security</li>
                <li>เมื่อท่านกด <strong>"Disconnect"</strong> ในแอป เราจะ revoke token กับ Google ทันทีและลบ token จากฐานข้อมูล</li>
                <li>เมื่อท่านปิดบัญชี HitzLink ข้อมูล Google ทั้งหมดของท่านจะถูกลบภายใน 30 วัน</li>
                <li>ท่านสามารถ revoke การ access ของเราได้ตลอดเวลาโดยตรงจาก Google ที่{' '}
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-300 hover:text-blue-200 underline"
                  >
                    myaccount.google.com/permissions
                  </a>
                </li>
              </ul>
            </div>

            {/* Limited Use Disclosure — VERBATIM English text required by
                Google. Keep in English (Google reviewers expect to find
                the exact phrase). Do not paraphrase or translate this
                paragraph. */}
            <div className="bg-emerald-900/20 border border-emerald-700/50 rounded p-4 mt-4">
              <p className="text-emerald-300 text-xs font-semibold mb-2 uppercase tracking-wide">
                Limited Use Disclosure
              </p>
              <p className="text-gray-200 text-xs leading-relaxed">
                HitzLink's use and transfer to any other app of information received from
                Google APIs will adhere to{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-emerald-300 hover:text-emerald-200 underline"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <div className="mt-6 pt-4 border-t border-gray-800">
          <div className="bg-green-900/20 border border-green-700/50 rounded p-4">
            <p className="text-white font-semibold text-sm mb-2">📧 ติดต่อเจ้าหน้าที่คุ้มครองข้อมูล (DPO)</p>
            <div className="text-xs text-gray-300 space-y-1">
              <p><strong>ผู้ควบคุมข้อมูลส่วนบุคคล (Data Controller):</strong> บริษัท ฮิตซ์เทค โซลูชั่นส์ จำกัด (Hitz Tech Solutions Co., Ltd.)</p>
              <p><strong>Email:</strong> dpo@hitzlink.com หรือ support@hitzlink.com</p>
              <p><strong>Response Time:</strong> ภายใน 30 วัน</p>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              หากไม่พอใจ สามารถยื่นเรื่องต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (PDPC)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

